import React, { Dispatch, ReactElement, SetStateAction, useEffect, useRef } from 'react';
import { formatCsvDataToColumns, MultipartResponse } from '../../helpers/multipart-response.helper';
import { DataframeDataRetrievalResponse, TDataframeOrder, TDateRange } from '../../api/types';
import {
	ISortBy,
	TableComposable,
	Tbody,
	Td,
	Th,
	Thead,
	ThProps,
	Tr,
} from '@patternfly/react-table';
import { TNewDataframeOrder } from '../../api/dataframes/Dataframes';
import Loader from '../util/Loader';
import { Bullseye, EmptyState, EmptyStateVariant, Title } from '@patternfly/react-core';
import { TUnitType, UnitType, UnitTypeFunctionEnum } from '../../api/analytics/UnitType';
import { IIndexable } from '../../types/general';
import { DraggableMenuItemData } from '../../types/databuilder/databuilder';
import { DateRange } from '../../api/date-period-selector/DateRange';
import { useApplication } from '../../../src/components/user/ApplicationProvider';
import { OptionsBuilderItemTypes } from '../../types/dataframes/options-builder-item-types';
import './Preview.scss';
import { numberFormatter } from '../../helpers/number-formatter';
import { containerDimensions } from '../../helpers/container-dimensions.helper';
import _ from 'lodash';
import { DatePeriodFormatter } from '../date-period-selector/DatePeriodFormatter';
import { faPlusSquare, faMinusSquare } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReportConditionalFormattingRuleEnum } from '../../enums/report-condition-formatting-rule';
import { Link, useLocation } from 'react-router-dom';
import { ConditionalRule } from '../../types/conditional-rules/conditional-rules';
import { ChartTypesEnum } from '../../enums/chart-types.enum';
import * as ReactDOMServer from 'react-dom/server';
import { useMount } from 'react-use';
import { isReallyEmpty } from '../../helpers/chart.helper';

export type IPreviewProps = {
	data?: MultipartResponse<DataframeDataRetrievalResponse>;
	facts?: DraggableMenuItemData[];
	rows?: DraggableMenuItemData[];
	drillRows?: DraggableMenuItemData[];
	drillFilters?: DraggableMenuItemData[];
	hasDateDrill?: boolean;
	columns?: DraggableMenuItemData[];
	unitTypes?: TUnitType[];
	order: (TDataframeOrder | TNewDataframeOrder)[];
	setOrder?: Dispatch<SetStateAction<(TDataframeOrder | TNewDataframeOrder)[]>>;
	isLoading: boolean;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
	setSelectedUnitType?: Dispatch<SetStateAction<TUnitType | undefined>>;
	ApplyDrilldownDrillIn?: (
		name: string,
		isKMF: boolean,
		column?: string,
		columnIdx?: number
	) => void;
	fromWidget?: boolean;
	tableWidgetLink?: string;
	title?: string;
	conditionalRules?: ConditionalRule[];
	chartType?: string;
	chartLoaded?: boolean;
	allowClickNavigate?: boolean;
	isColTotalsChecked?: boolean;
	isRowTotalsChecked?: boolean;
	dataframeDataId?: number;
	periodId?: number;
};

const Preview = (props: IPreviewProps): ReactElement => {
	const [columns, setColumns] = React.useState<string[][]>([]);
	const [formattedData, setFormattedData] = React.useState<IIndexable[]>([]);
	const [unformattedFactValues, setUnformattedFactValues] = React.useState<IIndexable[]>([]);
	const [sortBy, setSortBy] = React.useState<ISortBy[]>([]);
	const { currentDatePeriods } = useApplication();
	const defaultDatePeriod =
		currentDatePeriods.find((dp) => dp.period === 3) ?? (DateRange.Default() as TDateRange);

	const periodId = props.periodId ?? defaultDatePeriod?.period ?? 0;
	const containerRef = useRef<HTMLDivElement>(null);
	const containerDims = containerDimensions(containerRef);
	const headerRef = useRef<HTMLDivElement>(null);
	const headerDims = containerDimensions(headerRef);
	const [displayStateNoResults, setDisplayStateNoResults] = React.useState<string>('none');
	const pathname = useLocation().pathname;
	const isTableOrReportOrChart =
		pathname.includes('/report/') ||
		pathname.includes('/table/') ||
		pathname.includes('/chart/');
	const isTableReportView =
		pathname.includes('/report/view/') || pathname.includes('/table/view/');
	const [grandTotalRow, setGrandTotalRow] = React.useState<IIndexable>({});
	const [hasBreakoutGroups, setHasBreakoutGroups] = React.useState(false);
	const [unitConsistency, setUnitConsistency] = React.useState<Record<string, boolean>>({});
	const [unitTypesByKey, setUnitTypesByKey] = React.useState<Record<string, UnitType>>({});

	useMount(() => {
		if (props.fromWidget) {
			setDisplayStateNoResults('contents');
		}
	});

	useEffect(() => {
		const handler = setTimeout(() => {
			if (props.data) {
				if (props.data.json) {
					if (props.data.csvData) {
						props.setIsLoading(true);
						let formattedData: IIndexable[] = formatCsvDataToColumns(
							props.data.json.columns,
							props.data.csvData,
							periodId
						);
						const valuesBeforeFormatting: IIndexable[] = [];

						if (formattedData) {
							const seenValues: string[] = [];
							const hasDrillableItems = props.rows?.some(
								(row) => row.data?.drillable
							);
							formattedData = formattedData.map(
								(item: IIndexable, index: number): IIndexable => {
									const keys: string[] = Object.keys(item);
									const isSummaryRow = keys.some(
										(key) =>
											item[key] && (item[key] as string).includes('Summary')
									);
									const isGrandTotalRow = keys.some(
										(key) =>
											item[key] &&
											(item[key] as string).includes('Grand Total')
									);
									setHasBreakoutGroups(isSummaryRow || isGrandTotalRow);

									keys.map((key) => {
										const isPercentageOfTotalCol =
											key.includes('PercentofTotal') ||
											key.includes('Percent of Total');

										const fact =
											props.chartType === ChartTypesEnum.Columnrange &&
											props.facts
												? props.facts[0]
												: props.facts?.find((fact) => {
														const keyToUse = isPercentageOfTotalCol
															? key
																	.replace('PercentofTotal', '')
																	.replace('Percent of Total', '')
																	.toLowerCase()
																	.replace(/\s/g, '')
															: key
																	.toLowerCase()
																	.replace(/\s/g, '')
																	.replaceAll('"', '');
														const isAlias =
															keyToUse ===
															fact.data?.alias
																?.toLowerCase()
																.replace(/\s/g, '');
														const isTitle =
															keyToUse ===
															fact.data?.title
																.toLowerCase()
																.replace(/\s/g, '');
														return isAlias || isTitle;
												  });

										if (fact) {
											const unitType: TUnitType | undefined = props.unitTypes
												? props.unitTypes?.find(
														(type: TUnitType) =>
															type.id == fact?.data?.unitType
												  )
												: undefined;
											if (item[key] && item[key] != '') {
												if (!isNaN(item[key] as number)) {
													valuesBeforeFormatting.push({
														factId: fact.data?.id ?? 0,
														index: index,
														value: item[key] as number,
													});
													if (isPercentageOfTotalCol) {
														item[key] = `${numberFormatter(
															item[key] as number,
															fact?.data?.numDecimals,
															true
														)}%`;
													} else if (unitType) {
														if (
															unitType.name.toLowerCase() ===
															'percentage'
														) {
															item[key] = numberFormatter(
																parseFloat(item[key] as string) *
																	100,
																fact.data?.numDecimals,
																true
															);
														} else {
															item[key] = numberFormatter(
																parseFloat(item[key] as string),
																fact.data?.numDecimals,
																true
															);
														}

														item[key] = unitType.prefix
															? unitType.prefix +
															  (item[key] as string)
															: unitType.suffix
															? (item[key] as string) +
															  unitType.suffix
															: (item[key] as string);
													}
												}
											} else {
												if (fact.data?.defaultWhenNull) {
													item[key] = fact.data?.defaultWhenNull;
												}
											}
										} else {
											if (!isSummaryRow && !isGrandTotalRow) {
												const isBreakGroup = props.rows?.find(
													(row) =>
														row.data?.title.replace(/\s/g, '') === key
												)?.data?.totaled;
												const drillEnabled = props.rows?.find(
													(row) =>
														row.data?.title.replace(/\s/g, '') === key
												)?.data?.drillable;

												if (
													isBreakGroup ||
													(hasDrillableItems && drillEnabled)
												) {
													if (seenValues.includes(item[key] as string)) {
														item[key] = '';
													} else {
														seenValues.push(item[key] as string);
													}
												}
											} else {
												seenValues.length = 0;
											}
										}

										if (
											(isSummaryRow || isGrandTotalRow) &&
											item[key] &&
											item[key] != ''
										) {
											item[key] = <b>{item[key]}</b>;
										}
									});

									return item;
								}
							);

							// Build the grand total row
							if (formattedData.length > 0) {
								const firstRow = formattedData[0];
								const keys = Object.keys(firstRow).filter(
									(k) => k !== '__rowTotal'
								); // exclude old totals

								// Compute each row's total
								formattedData.forEach((row) => {
									if (
										!hasBreakoutGroups &&
										isTableOrReportOrChart &&
										props.isRowTotalsChecked
									) {
										const rowValues: number[] = [];

										keys.forEach((key, index) => {
											if (index === 0) return; // skip label column
											// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
											const raw = row[key];

											// Extract number from any formatted string
											const numeric =
												typeof raw === 'string'
													? parseFloat(raw.replace(/[^0-9.-]/g, ''))
													: typeof raw === 'number'
													? raw
													: 0;

											if (!isNaN(numeric)) {
												rowValues.push(numeric);
											}
										});

										// Determine the fact for this row so we know what function to apply
										const fact = props.facts?.find((f) => {
											return keys.some((key) => {
												const keyName = key
													?.toLowerCase()
													.replace(/\s/g, '');

												const title = f.data?.title
													?.toLowerCase()
													.replace(/\s/g, '');
												const alias = f.data?.alias
													?.toLowerCase()
													.replace(/\s/g, '');

												return title === keyName || alias === keyName;
											});
										});

										let unitType = props.unitTypes?.find(
											(t) => t.id === fact?.data?.unitType
										);

										if (!unitType) {
											const fact1UnitType = props.unitTypes?.find(
												(t) =>
													t.id ===
													(props.facts && props.facts[0].data?.unitType)
											);
											const fact2UnitType = props.unitTypes?.find(
												(t) =>
													t.id ===
													(props.facts && props.facts[1].data?.unitType)
											);
											const sameUnitTypes = fact1UnitType == fact2UnitType;
											if (sameUnitTypes) {
												unitType = fact1UnitType;
											}
										}

										const isDateRange =
											props.rows &&
											props.rows.length > 0 &&
											props.rows[0].entityType ==
												OptionsBuilderItemTypes.DateSeries;

										const func = isDateRange
											? unitType?.function_time_series
											: unitType?.function_dimension_series;

										// Apply the function using your new function
										const calculated = calculateFunctionValue(
											rowValues,
											func ?? ('sum' as UnitTypeFunctionEnum),
											fact?.data?.numDecimals,
											unitType
										);

										row['__rowTotal'] = calculated;
									} else {
										delete row['__rowTotal']; // remove if hiding
									}
								});

								// Build grand total row
								const localGrandTotalRow: IIndexable = {};

								keys.forEach((key, index) => {
									if (index === 0) {
										localGrandTotalRow[key] = 'Totals';
									} else {
										// Identify the fact for this column
										const fact = props.facts?.find((f) => {
											const cleanedTitle = key
												.toLowerCase()
												.replace(/\s/g, '');
											return (
												f.data?.title.toLowerCase().replace(/\s/g, '') ===
													cleanedTitle ||
												f.data?.alias?.toLowerCase().replace(/\s/g, '') ===
													cleanedTitle
											);
										});

										const unitType: TUnitType | undefined = props.unitTypes
											? props.unitTypes?.find(
													(type: TUnitType) =>
														type.id == fact?.data?.unitType
											  )
											: undefined;

										const isDateRange =
											props.rows &&
											props.rows.length > 0 &&
											props.rows[0].entityType ==
												OptionsBuilderItemTypes.DateSeries;

										// Determine math function
										const func = isDateRange
											? unitType?.function_time_series
											: unitType?.function_dimension_series;

										// Collect numeric values for this column
										const numericValues: number[] = formattedData
											.map((row) => {
												// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
												const raw = row[key];
												const numeric =
													typeof raw === 'string'
														? parseFloat(raw.replace(/[^0-9.]/g, ''))
														: typeof raw === 'number'
														? raw
														: 0;
												return isNaN(numeric) ? null : numeric;
											})
											.filter((v): v is number => v !== null);

										// Apply the math function
										let computed: string | number = 0;

										if (func) {
											computed = calculateFunctionValue(
												numericValues,
												func,
												fact?.data?.numDecimals,
												unitType
											);
										} else {
											// fallback to sum
											computed = numericValues.reduce((a, b) => a + b, 0);
										}

										localGrandTotalRow[key] =
											numericValues.length === 0 ? '' : computed;
									}
								});

								// Add grand total of the totals column
								if (!isTableReportView) {
									localGrandTotalRow['__rowTotal'] = formattedData.reduce(
										(acc, row) => acc + ((row['__rowTotal'] as number) || 0),
										0
									);
								}

								setGrandTotalRow(localGrandTotalRow);

								// Compute which columns have consistent units
								const unitConsistency: Record<string, boolean> = {};
								const unitTypesByKeyLocal: Record<string, UnitType> = {};

								if (formattedData.length) {
									const sampleRow = formattedData[0];
									const dataKeys = Object.keys(sampleRow).filter(
										(k) => k !== '__rowTotal'
									);

									dataKeys.forEach((key) => {
										const unitPatterns = new Set<string>();

										formattedData.forEach((row) => {
											// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
											const raw = row[key];

											const fact = props.facts?.find((f) => {
												const keyName = key
													.toLowerCase()
													.replace(/\s/g, '');
												const title = f.data?.title
													?.toLowerCase()
													.replace(/\s/g, '');
												const alias = f.data?.alias
													?.toLowerCase()
													.replace(/\s/g, '');
												return title === keyName || alias === keyName;
											});

											const unitType = props.unitTypes?.find(
												(t) => t.id === fact?.data?.unitType
											);

											// ---- UNIT TYPE CONSISTENCY (mirrors unitPatterns logic)
											if (
												raw != null &&
												raw !== '' &&
												unitType &&
												unitType.function_dimension_series
											) {
												unitTypesByKeyLocal[key] = unitType;
											}

											// ---- PREFIX / SUFFIX CONSISTENCY (unchanged)
											if (raw != null && raw !== '') {
												const { prefix, suffix } = getPrefixSuffix(
													String(raw)
												);
												// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
												const hasLetters = /[a-zA-Z]/.test(raw);

												if (prefix && suffix && !hasLetters) {
													unitPatterns.add(prefix + '|' + suffix);
												}
											}
										});

										unitConsistency[key] = unitPatterns.size <= 1;
									});

									setUnitConsistency(unitConsistency);
								}
								setUnitTypesByKey(unitTypesByKeyLocal);
							}

							// If there are pivots then format the date range pivot
							// All other pivots are not numeric so we only need to cater for the date range item
							const colsToUse = props.data.json.columns.map(
								(colList, index, allColumns) => {
									return colList.map((col) => {
										// Remove any leading/trailing quotes or escape characters
										const cleanCol =
											typeof col === 'string'
												? col.replace(/^"+|"+$/g, '').replace(/\\/g, '')
												: col;

										if (index < allColumns.length - 1) {
											return isNaN(+cleanCol)
												? cleanCol
												: DatePeriodFormatter(cleanCol, periodId, true);
										}
										return cleanCol;
									});
								}
							);

							if (props.order.length && props.order.length === colsToUse.length) {
								const newSortBy = props.order.map((order, index) => {
									return {
										index: getSortIndex(
											props.facts ?? [],
											props.rows ?? [],
											props.columns ?? [],
											order,
											index,
											colsToUse
										),
										direction: order.direction.toLowerCase() as
											| 'asc'
											| 'desc'
											| undefined,
										defaultDirection: 'asc',
									};
								});

								// Check if the order objects are not equal so that the data retrieval is not continuously triggered
								if (!_.isEqual(sortBy, newSortBy)) {
									setSortBy(newSortBy as ISortBy[]);
								}
							} else {
								setSortBy(
									colsToUse.map(() => {
										return {
											index: 0,
											direction: 'asc',
											defaultDirection: 'asc',
										};
									})
								);
							}

							setUnformattedFactValues(valuesBeforeFormatting);
							setFormattedData(formattedData);
							setColumns(colsToUse);
							props.setIsLoading(false);
						}
					} else {
						setFormattedData([]);
					}
				}
			} else {
				setFormattedData([]);
			}
		}, 500);

		return () => {
			clearTimeout(handler);
		};
	}, [
		props.data,
		props.setSelectedUnitType,
		props.chartType,
		props.conditionalRules,
		props.isRowTotalsChecked,
	]);

	useEffect(() => {
		if (props.fromWidget) {
			return;
		}

		if (formattedData.length == 0) {
			props.setIsLoading(true);
			//allow for 1 second before showing no results panel
			setTimeout(() => {
				setDisplayStateNoResults('contents');
				props.setIsLoading(false);
			}, 2500);
		}
	}, [formattedData]);

	const getConditionalFormattingStyles = (
		factId: number,
		dataframeDataId: number | undefined,
		index: number
	) => {
		const factValue = unformattedFactValues.find(
			(val) => val.factId === factId && val.index === index
		);
		// Previously filtered only by key_measure_fact_id; prefer dataframe_data_id when present
		const rules = props.conditionalRules?.filter((rule) =>
			rule.dataframe_data_id != null
				? rule.dataframe_data_id === dataframeDataId
				: rule.key_measure_fact_id === factId
		);
		let styles: IIndexable | null = null;
		let htmlToUse: string | null = null;

		if (factValue) {
			rules?.forEach((rule) => {
				switch (rule.rule) {
					case ReportConditionalFormattingRuleEnum.BETWEEN:
						if (rule.value2) {
							if (+factValue.value > rule.value && +factValue.value < rule.value2) {
								styles = convertStyleStringToObject(rule.editorHTML);
								htmlToUse = rule.editorHTML;
							}
						}
						break;
					case ReportConditionalFormattingRuleEnum.EQUAL_TO:
						if (+factValue.value === rule.value) {
							styles = convertStyleStringToObject(rule.editorHTML);
							htmlToUse = rule.editorHTML;
						}
						break;
					case ReportConditionalFormattingRuleEnum.GREATER_THAN:
						if (+factValue.value > rule.value) {
							styles = convertStyleStringToObject(rule.editorHTML);
							htmlToUse = rule.editorHTML;
						}
						break;
					case ReportConditionalFormattingRuleEnum.GREATER_THAN_OR_EQUAL_TO:
						if (+factValue.value >= rule.value) {
							styles = convertStyleStringToObject(rule.editorHTML);
							htmlToUse = rule.editorHTML;
						}
						break;
					case ReportConditionalFormattingRuleEnum.LESS_THAN:
						if (+factValue.value < rule.value) {
							styles = convertStyleStringToObject(rule.editorHTML);
							htmlToUse = rule.editorHTML;
						}
						break;
					case ReportConditionalFormattingRuleEnum.LESS_THAN_OR_EQUAL_TO:
						if (+factValue.value <= rule.value) {
							styles = convertStyleStringToObject(rule.editorHTML);
							htmlToUse = rule.editorHTML;
						}
						break;
					case ReportConditionalFormattingRuleEnum.NOT_EQUAL_TO:
						if (+factValue.value != rule.value) {
							styles = convertStyleStringToObject(rule.editorHTML);
							htmlToUse = rule.editorHTML;
						}
						break;
				}
			});
		}

		return { styles, htmlToUse };
	};

	const convertStyleStringToObject = (html: string): IIndexable | null => {
		const regex = /style="([^"]*)"/;
		const match = html.match(regex);

		if (match) {
			const styleValue = match[1];
			return styleValue.split(';').reduce((acc, style) => {
				if (style.trim()) {
					const [key, value] = style.split(':');
					const camelCaseKey = key
						.trim()
						.replace(/-([a-z])/g, (_, char) => (char as string).toUpperCase());
					acc[camelCaseKey] = value.trim();
				}
				return acc;
			}, {} as Record<string, string>);
		}

		return null;
	};

	const getSortIndex = (
		facts: DraggableMenuItemData[],
		rows: DraggableMenuItemData[],
		columns: DraggableMenuItemData[],
		order: TDataframeOrder | TNewDataframeOrder,
		level: number,
		colArray: string[][]
	) => {
		if (
			columns.find(
				(col) => col.data?.id === order.entity_id && col.entityType === order.entity_type
			)
		) {
			return 0;
		}

		const colsByLevel = colArray[level];

		let item = rows.find(
			(row) => row.data?.id === order.entity_id && row.entityType === order.entity_type
		);

		if (!item) {
			item = facts.find(
				(fact) => fact.data?.id === order.entity_id && fact.entityType === order.entity_type
			);

			// If pivots exist and the sorted column is a fact then make use of the pivotSortIndex property to ensure correct sorted column
			if (item && colArray.length > 1) {
				return order.pivotSortIndex;
			}
		}

		if (item) {
			return colsByLevel.indexOf(item.data?.title ?? '');
		}

		return 0;
	};

	const getSortParams = (columnIndex: number, level: number): ThProps['sort'] => {
		return {
			sortBy: sortBy[level],
			onSort: (
				event: React.MouseEvent,
				index: number,
				direction: 'asc' | 'desc' | undefined
			) => {
				const newSortBy = [...sortBy];
				const existingSort = newSortBy[level];
				existingSort.index = index;
				existingSort.direction = direction;
				setSortBy(newSortBy);
				setOrderObject(newSortBy);
			},
			columnIndex,
		};
	};

	const setOrderObject = (sortBy: ISortBy[]) => {
		if (props.setOrder) {
			const order: TNewDataframeOrder[] = [];

			sortBy.forEach((sort, index) => {
				let pivotSort = '';
				let item = props.facts?.find((fact) => {
					return (
						columns.length > 0 && fact.data?.title === columns[index][sort.index ?? 0]
					);
				});

				if (item && item.data?.calculated) {
					item.entityType = OptionsBuilderItemTypes.CalculatedFact;
					item.data.id = item.data.dataframeDataId;
				}

				// If pivots exist and sorted column is a fact then build up this piped string for the BE to utilize
				if (item && columns.length > 1) {
					for (let x = columns.length - 2; x >= 0; x--) {
						for (let i = sort.index ?? 0; i > 0; i--) {
							const currentCol = columns[x][i];
							if (currentCol !== '') {
								pivotSort = pivotSort ? `${currentCol}|${pivotSort}` : currentCol;
								break;
							}
						}
					}
				}

				if (!item) {
					item = props.rows?.find((row) => {
						return (
							columns.length > 0 &&
							row.data?.title === columns[index][sort.index ?? 0]
						);
					});
				}

				if (!item) {
					item = props.columns?.find((col) => {
						return (
							columns.length > 0 &&
							col.data?.title === columns[index][sort.index ?? 0]
						);
					});
				}

				if (!item) {
					item = props.rows?.find((row) => {
						return row.entityType == OptionsBuilderItemTypes.DateSeries;
					});
				}

				if (item) {
					order.push({
						direction: sort.direction?.toUpperCase() ?? '',
						entity_id: item.data?.id ? +item.data.id : 0,
						entity_type: item.entityType,
						useSequence: item.data?.useSequence ?? false,
						pivotSort,
						pivotSortIndex: pivotSort != '' ? sort.index : undefined,
					});
				}
			});

			if (
				order.length &&
				!_.isEqual(
					order,
					props.order.map((o) => {
						return {
							direction: o.direction,
							entity_id: o.entity_id,
							entity_type: o.entity_type,
							useSequence: o.useSequence,
							pivotSort: o.pivotSort,
							pivotSortIndex: o.pivotSort != '' ? o.pivotSortIndex : undefined,
						};
					})
				)
			) {
				props.setOrder(order);
			}
		}
	};
	const styleValue =
		props.chartLoaded !== undefined
			? props.chartLoaded == true
				? { display: 'contents' }
				: { display: 'none' }
			: { display: 'contents' };

	const noData =
		props.isLoading || !props.chartLoaded ? (
			<Loader />
		) : (
			<Tr style={{ display: displayStateNoResults }}>
				<Td colSpan={100}>
					<Bullseye>
						<EmptyState variant={EmptyStateVariant.small}>
							<Title
								headingLevel="h2"
								size="lg"
							>
								No results found
							</Title>
						</EmptyState>
					</Bullseye>
				</Td>
			</Tr>
		);

	const calculateFunctionValue = (
		values: number[],
		func: UnitTypeFunctionEnum,
		numDecimals: number | undefined,
		unitType: TUnitType | undefined
	) => {
		const decimals = typeof numDecimals === 'number' ? numDecimals : 0;

		let result = 0;

		switch (func) {
			case 'avg':
				result = values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
				break;

			case 'cnt':
				result = values.length;
				break;

			case 'sum':
				result = values.reduce((a, b) => a + b, 0);
				break;

			case 'min':
				result = values.length ? Math.min(...values) : 0;
				break;

			case 'max':
				result = values.length ? Math.max(...values) : 0;
				break;

			default:
				result = 0;
		}

		// Apply decimals and return as NUMBER
		const fixed = `${unitType?.prefix ?? ''}${result.toFixed(decimals)}${
			unitType?.suffix ?? ''
		}`;

		return fixed;
	};

	const getColumns = (currentRowColumns: string[], level: number, showSort = true) => {
		const isPivotItems = currentRowColumns.some((row) =>
			props.columns?.some((col) => col.data?.title === row)
		);
		const columns: {
			col: string;
			colSpan: number;
			sortParams: ThProps['sort'];
			isPercentageCol: boolean;
		}[] = [];
		currentRowColumns.map((col, index) => {
			const sortParams = getSortParams(index, level);
			if (!_.isEmpty(col)) {
				const isPercentageCol = col.replace(/\s+/g, '').includes('PercentofTotal');
				const thisColumn = {
					col: col,
					colSpan: 1,
					sortParams: sortParams,
					isPercentageCol,
				};

				// If has pivots
				if (index !== 0 && !showSort) {
					thisColumn.sortParams = undefined;
				}
				columns.push(thisColumn);
			} else {
				columns[columns.length - 1].colSpan = columns[columns.length - 1].colSpan + 1;
			}
		});

		return (
			<Tr>
				{columns.map((col, index) => {
					const isLastRow = props.rows && index >= props.rows?.length - 1;
					const isPivotHeader = isPivotItems && index === 0;

					let isNumeric = false;

					if (props.data && props.data.json && props.data.csvData) {
						const formattedData: IIndexable[] = formatCsvDataToColumns(
							props.data?.json?.columns,
							props.data?.csvData,
							periodId
						);

						isNumeric = !!formattedData.find((data) => {
							// eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment
							const value = data[col.col.replaceAll(/\s/g, '')];
							return value
								? !isNaN(
										// eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
										value.replace(/[^a-zA-Z0-9]/g, '')
								  )
								: false;
						});
					}

					return (
						<Th
							key={index}
							className={`${col.col.replaceAll(/\s/g, '')} ${
								isPivotHeader ? 'pivot-header' : ''
							}`}
							colSpan={col.colSpan}
							sort={col.isPercentageCol ? undefined : col.sortParams}
							textCenter={isNumeric || col.col == 'Totals'}
							hasLeftBorder={isLastRow && col.colSpan > 1}
							hasRightBorder={isLastRow && col.colSpan > 1}
						>
							{col.col}
						</Th>
					);
				})}
				{!hasBreakoutGroups && isTableOrReportOrChart && props.isRowTotalsChecked && (
					<Th className="pf-m-center">Summary</Th>
				)}
			</Tr>
		);
	};

	const getDrillIcon = (col: string) => {
		if (props.drillRows && props.drillFilters) {
			const drillRow = props.drillRows.find(
				(row) => row.data?.title.replace(/\s/g, '') === col
			);
			if (drillRow) {
				const exists = props.drillFilters.find(
					(filter) => filter.data?.title.replace(/\s/g, '') === col
				);

				if (drillRow.entityType === OptionsBuilderItemTypes.DateSeries) {
					return (
						<>
							<FontAwesomeIcon
								icon={props.hasDateDrill ? faMinusSquare : faPlusSquare}
								size="1x"
							/>
							&nbsp;
						</>
					);
				} else {
					return (
						<>
							<FontAwesomeIcon
								icon={exists ? faMinusSquare : faPlusSquare}
								size="1x"
							/>
							&nbsp;
						</>
					);
				}
			}
		}
	};

	const formatNumber = (value: any) => {
		//eslint-disable-next-line @typescript-eslint/no-unsafe-return
		if (value == null || value === '') return value;
		const num = Number(value);
		//eslint-disable-next-line @typescript-eslint/no-unsafe-return
		return isNaN(num) ? value : num.toLocaleString();
	};

	const getPrefixSuffix = (raw: string) => {
		if (!raw || typeof raw !== 'string') return { prefix: '', suffix: '' };

		const prefixMatch = raw.match(/^[^\d.-]+/); // stuff before number
		const suffixMatch = raw.match(/[^\d.-]+$/); // stuff after number

		return {
			prefix: prefixMatch ? prefixMatch[0] : '',
			suffix: suffixMatch ? suffixMatch[0] : '',
		};
	};

	const getCells = (row: IIndexable, rowIndex: number) => {
		const isGrandTotalRow = row === grandTotalRow;

		// Get all real data columns (exclude totals)
		const baseKeys = Object.keys(row).filter((k) => k !== '__rowTotal');

		const hideTotals = hasBreakoutGroups;

		const colsToUse =
			hideTotals && props.isColTotalsChecked ? [...baseKeys] : [...baseKeys, '__rowTotal'];

		return colsToUse.map((key, colIndex) => {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			let value = row[key];

			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			const updatedVal =
				typeof value === 'string' ? value.replace(/[^a-zA-Z0-9.]/g, '') : value;

			const isNumeric = value != null && !isNaN(updatedVal ? +updatedVal : 0);

			let formatting: IIndexable | null = null;

			if (props.conditionalRules?.length) {
				let fact = props.facts?.find((f) => f.data?.title.replace(/\s/g, '') === key);

				const normalize = (val?: string) =>
					(val ?? '')
						.replace(/"/g, '') // remove quotes
						.replace(/\s+/g, '') // remove all whitespace
						.toLowerCase();

				if (!fact) {
					const normalizedKey = normalize(key);

					fact = props.facts?.find((f) => {
						const title = normalize(f.data?.title);
						const alias = normalize(f.data?.alias);

						return title === normalizedKey || alias === normalizedKey;
					});
				}
				if (fact) {
					formatting = getConditionalFormattingStyles(
						fact.data?.id ?? 0,
						fact.data?.dataframeDataId,
						rowIndex
					);
				}
			}

			// Hide totals column conditionally
			if (
				key === '__rowTotal' &&
				props.isColTotalsChecked &&
				!props.isRowTotalsChecked &&
				!hasBreakoutGroups
			) {
				return null;
			}

			if (isGrandTotalRow && key === '__rowTotal') {
				return null; // Skip rendering the last <Td> in grand total row
			}

			//fix for grand total row when value is not numeric
			if (value == 0 || value == null || value == undefined) {
				value = '';
			}

			// Default content
			let cellContent: React.ReactElement = <>{value ?? ''}</>;

			// Summary logic override (row-level totals)
			if (!hideTotals && key === '__rowTotal') {
				const allUnitTypesConsistent = Object.values(unitConsistency).every((v) => v);
				const unitTypes = Object.values(unitTypesByKey);
				const allColumnRowUnitTypesConsistent =
					unitTypes.length === 0
						? true
						: unitTypes.every(
								(unitType) =>
									unitType.function_dimension_series ===
									unitTypes[0].function_dimension_series
						  );

				const allTrue = allUnitTypesConsistent && allColumnRowUnitTypesConsistent;

				// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
				cellContent = allTrue ? <b>{`${formatNumber(value)}`}</b> : <b>***</b>;
			} else if (isGrandTotalRow) {
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				let actualValue = value;
				if (value === 'Totals') actualValue = 'Summary';
				cellContent = <b>{formatNumber(actualValue)}</b>;
			}

			return (
				<Td
					key={key + colIndex.toString()}
					dataLabel={key}
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
					textCenter={isNumeric}
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
					style={formatting?.styles ?? undefined}
					className={`${props.ApplyDrilldownDrillIn ? 'cursor' : ''}`}
					onClick={() => {
						applyTableDrilldown(
							value as string,
							props.facts?.some(
								(fact) => fact.data?.title.replace(/\s/g, '') === key
							) ?? false,
							key,
							colIndex
						);
					}}
				>
					{!isReallyEmpty(value) ? (
						<>
							{getDrillIcon(key)}

							{formatting?.htmlToUse ? (
								<span
									dangerouslySetInnerHTML={{
										// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
										__html: formatting.htmlToUse.replace(
											'{0}',
											ReactDOMServer.renderToString(cellContent)
										),
									}}
								/>
							) : (
								cellContent
							)}
						</>
					) : (
						(() => {
							const fact = props.facts?.find(
								(f) => f.data?.title.replace(/\s/g, '') === key
							);
							return fact?.data?.defaultWhenNull;
						})()
					)}
				</Td>
			);
		});
	};

	const applyTableDrilldown = (
		filterValue: string,
		isKMF = false,
		col?: string,
		colIdx?: number
	) => {
		props.ApplyDrilldownDrillIn && props.ApplyDrilldownDrillIn(filterValue, isKMF, col, colIdx);
	};

	return (
		<div
			className={`preview-table-container ${props.fromWidget ? 'widget-height' : ''}`}
			ref={containerRef}
		>
			{props.title && (
				<h1
					className="preview-title"
					ref={headerRef}
				>
					{props.allowClickNavigate ? (
						<Link
							className="table-hyperlink"
							to={props.tableWidgetLink ? props.tableWidgetLink : ''}
						>
							{props.title}
						</Link>
					) : (
						<div className="table-hyperlink">{props.title}</div>
					)}
				</h1>
			)}
			<div
				className={!isTableReportView ? 'scroll-container' : 'scroll-view-only-container'}
				style={{
					maxWidth: containerDims?.width,
					...(props.fromWidget && {
						maxHeight: containerDims
							? containerDims?.height - (headerDims?.height ?? 0)
							: undefined,
					}),
				}}
			>
				{props.isLoading || !props.chartLoaded ? (
					<Loader />
				) : (
					<TableComposable>
						<Thead className="sticky-header">
							{columns.map((col, index) =>
								getColumns(col, index, index === columns.length - 1)
							)}
						</Thead>
						<Tbody style={{ display: displayStateNoResults }}>
							{formattedData.length > 0
								? formattedData.map((row, rowIndex) => {
										return <Tr key={rowIndex}>{getCells(row, rowIndex)}</Tr>;
								  })
								: noData}
							{/* Add the Grand Total row */}
							{!hasBreakoutGroups &&
								isTableOrReportOrChart &&
								props.isColTotalsChecked && (
									<Tr key="grand-total-row">
										{getCells(grandTotalRow, formattedData.length)}
									</Tr>
								)}
						</Tbody>
					</TableComposable>
				)}
			</div>
		</div>
	);
};

export default Preview;
